// components/GiftModal.tsx
import React, { useState } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { useCanvaAuth } from '../contexts/CanvaAuthContext2.tsx';

type GiftModalProps = {
  show: boolean;
  onClose: () => void;
};

const GiftModal: React.FC<GiftModalProps> = ({ show, onClose }) => {
  const [modalStep, setModalStep] = useState(0);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fade, setFade] = useState(true);
  const { canvaAuthenticated,token } = useCanvaAuth();

  const env = canvaAuthenticated?.env;

  const handleFadeChange = (nextStep: number) => {
    setFade(false);
    setTimeout(() => {
      setModalStep(nextStep);
      setFade(true);
    }, 200);
  };

  const validateEmail = (email: string): { isValid: boolean; error?: string } => {
    const trimmedEmail = email.trim();
    
    // Empty check
    if (!trimmedEmail) {
      return { isValid: false, error: 'Please enter your email address' };
    }

    // Length checks (RFC 5321: local part max 64, domain max 255, total max 254)
    if (trimmedEmail.length > 254) {
      return { isValid: false, error: 'Email address is too long (maximum 254 characters)' };
    }

    // Basic structure check - must contain @
    if (!trimmedEmail.includes('@')) {
      return { isValid: false, error: 'Email must contain @ symbol' };
    }

    const parts = trimmedEmail.split('@');
    
    // Must have exactly one @
    if (parts.length !== 2) {
      return { isValid: false, error: 'Email must contain exactly one @ symbol' };
    }

    const [localPart, domain] = parts;

    // Local part checks
    if (!localPart || localPart.length === 0) {
      return { isValid: false, error: 'Email must have a local part before @' };
    }

    if (localPart.length > 64) {
      return { isValid: false, error: 'Local part of email is too long (maximum 64 characters)' };
    }

    // No leading or trailing dots in local part
    if (localPart.startsWith('.') || localPart.endsWith('.')) {
      return { isValid: false, error: 'Email cannot start or end with a dot' };
    }

    // No consecutive dots
    if (localPart.includes('..')) {
      return { isValid: false, error: 'Email cannot contain consecutive dots' };
    }

    // Domain checks
    if (!domain || domain.length === 0) {
      return { isValid: false, error: 'Email must have a domain after @' };
    }

    if (domain.length > 255) {
      return { isValid: false, error: 'Domain part of email is too long (maximum 255 characters)' };
    }

    // Domain must contain at least one dot
    if (!domain.includes('.')) {
      return { isValid: false, error: 'Domain must contain at least one dot (e.g., example.com)' };
    }

    // No leading or trailing dots in domain
    if (domain.startsWith('.') || domain.endsWith('.')) {
      return { isValid: false, error: 'Domain cannot start or end with a dot' };
    }

    // No consecutive dots in domain
    if (domain.includes('..')) {
      return { isValid: false, error: 'Domain cannot contain consecutive dots' };
    }

    // Check for valid TLD (at least 2 characters after last dot)
    const domainParts = domain.split('.');
    const tld = domainParts[domainParts.length - 1];
    if (!tld || tld.length < 2) {
      return { isValid: false, error: 'Domain must have a valid top-level domain (e.g., .com, .org)' };
    }

    // TLD should only contain letters
    if (!/^[a-zA-Z]+$/.test(tld)) {
      return { isValid: false, error: 'Top-level domain must contain only letters' };
    }

    // Character validation - RFC 5322 compliant pattern
    // Local part: allows letters, numbers, and these special chars: . ! # $ % & ' * + - / = ? ^ _ ` { | } ~
    // Domain: allows letters, numbers, dots, and hyphens
    const localPartRegex = /^[a-zA-Z0-9.!#$%&'*+\-/=?^_`{|}~]+$/;
    const domainRegex = /^[a-zA-Z0-9.-]+$/;

    if (!localPartRegex.test(localPart)) {
      return { isValid: false, error: 'Local part contains invalid characters' };
    }

    if (!domainRegex.test(domain)) {
      return { isValid: false, error: 'Domain contains invalid characters' };
    }

    // Check for common typos in TLD (only invalid ones)
    const commonTypos: { [key: string]: string } = {
      'con': 'com',
      'cpm': 'com',
      'cm': 'com',
      'om': 'com',
      'coom': 'com',
      'ogr': 'org',
      'nrt': 'net',
      'ner': 'net',
    };

    const lowerTld = tld.toLowerCase();
    if (commonTypos[lowerTld]) {
      return { isValid: false, error: `Did you mean .${commonTypos[lowerTld]}?` };
    }

    // Check for spaces anywhere
    if (trimmedEmail.includes(' ')) {
      return { isValid: false, error: 'Email cannot contain spaces' };
    }

    // Final comprehensive regex check (more permissive but catches edge cases)
    const comprehensiveEmailRegex = /^[a-zA-Z0-9.!#$%&'*+\-/=?^_`{|}~]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
    
    if (!comprehensiveEmailRegex.test(trimmedEmail)) {
      return { isValid: false, error: 'Please enter a valid email address' };
    }

    // Check for obviously fake patterns
    const fakePatterns = [
      /^test@/i,
      /^admin@/i,
      /^noreply@/i,
      /^no-reply@/i,
      /@example\./i,
      /@test\./i,
      /@localhost/i,
      /@123\./i,
    ];

    // Allow these patterns but warn (or you can block them)
    // For now, we'll allow them but you can uncomment to block:
    // for (const pattern of fakePatterns) {
    //   if (pattern.test(trimmedEmail)) {
    //     return { isValid: false, error: 'Please use a valid personal or business email' };
    //   }
    // }

    return { isValid: true };
  };

  const handleSubmitEmailAndClaimGift = async () => {
    setEmailError('');
    
    // Comprehensive email validation
    const validation = validateEmail(email);
    if (!validation.isValid) {
      setEmailError(validation.error || 'Please enter a valid email address');
      return;
    }

    if (!token || !env?.claimGiftUrl) {
      setEmailError('Unable to process request. Please try again.');
      return;
    }

    setIsSubmitting(true);

    try {
      // Claim gift with email - if frontend validation passes, grant the gift
      await fetch(env.claimGiftUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          giftId: canvaAuthenticated?.userProfile?.gift,
          email: email.trim()
        })
      });
      handleFadeChange(1);
    } catch (error) {
      console.error('Failed to claim gift:', error);
      setEmailError('Failed to claim gift. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={onClose} contentClassName="gift-modal" centered backdrop={true}>
      <div className={`transition-opacity ${fade ? 'opacity-100' : 'opacity-0'} transition duration-300`}>
        <Modal.Header>
          <Modal.Title>
            {modalStep === 0 ? (env?.strings['gift-modal-page-1-title'] || '🎉 A Little Gift for You') :
             (env?.strings['gift-modal-page-3-title'] || '🙏 Thank You')}
          </Modal.Title>
          <Button variant="close" aria-label="Close" onClick={onClose} />
        </Modal.Header>

        <Modal.Body>
          {modalStep === 0 && (
            <>
              <div className="text-center px-2 mb-3">
                <h5 className="mb-3">{env?.strings['gift-modal-page-1-line-1']}</h5>
                <p className="mb-2">
                  {env?.strings['gift-modal-page-1-line-2']}
                </p>
                <p className="mb-3">
                  <strong>{env?.strings['gift-modal-page-1-line-3']}</strong>
                </p>
              </div>
              <Form>
                <Form.Group className="mb-3">
                  <Form.Control
                    type="email"
                    placeholder={env?.strings['gift-modal-email-placeholder'] || "your.email@example.com"}
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setEmailError('');
                    }}
                    isInvalid={!!emailError}
                  />
                  {emailError && (
                    <Form.Control.Feedback type="invalid">
                      {emailError}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
                <div className="d-flex justify-content-center">
                  <Button 
                    variant="primary" 
                    onClick={handleSubmitEmailAndClaimGift}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        {env?.strings['gift-modal-claiming-text'] || 'Claiming...'}
                      </>
                    ) : (
                      <>
                        {env?.strings['gift-modal-page-1-btn-claim'] || '🎁 Claim Your Gift'}
                      </>
                    )}
                  </Button>
                </div>
              </Form>
            </>
          )}

          {modalStep === 1 && (
            <div className="text-center px-2">
              <p>{env?.strings['gift-modal-page-3-line-1'] || 'Thanks a lot! We really appreciate you using AI Music.'}</p>
              <Button variant="primary" onClick={onClose}>{env?.strings['gift-modal-close-btn'] || 'Close'}</Button>
            </div>
          )}
        </Modal.Body>
      </div>
    </Modal>
  );
};

export default GiftModal;
